import React, { useContext, useEffect, useState } from 'react'
import { API, Endpoints } from '../api/API';
import { arrayIannaTimeZones } from '../data/timezones';
import { BasicInfo, Facility, Institution, LanguageNames } from '../interfaces/Interfaces';
import { verifyName, verifyPhone } from '../js/verificationFunctions';
import APPSETTINGS from '../appsettings.json';
import SaveProfileModal from './SaveProfileModal';
import './ProfileEdit.css';
import { userDataCtx } from '../hooks/applicationData';
import { doesAppRequireAdditionalClaims, getProfileEditDisplay, isAppDataValid } from './Applications';

interface ProfileEditProps {
    lang: string | null,
    setLang: React.Dispatch<React.SetStateAction<string | null>>,
    languageValues: LanguageNames,
    userCountry: string | null,
    setUserCountry: React.Dispatch<React.SetStateAction<string | null>>,
    objectId: string | null,
    appId: string | null,
    translate: () => void,
    setBackgroundWIP: React.Dispatch<React.SetStateAction<boolean>>,
    setDisplayName: React.Dispatch<React.SetStateAction<string>>,
    email: string,
    basicInfo: BasicInfo,
    setBasicInfo: React.Dispatch<React.SetStateAction<BasicInfo>>,
    bdBioSciencesUserType: string | null,
    setBdBioSciencesUserType: React.Dispatch<React.SetStateAction<string | null>>,
    positions: Array<[string, string]>,
    getJWTDriver: (resetPassword: boolean) => Promise<boolean>,
    facilityInfo: Array<Facility>,
    facilityOptional: boolean,
    skipOptionalFacility: boolean,
    institutionLinkAlwaysOptional:boolean,
    skipOptionalInstitutionLink: boolean,
    hideFields: Array<string>,
    optionalFields: Array<string>,
    facZip: string,
    setFacZip: React.Dispatch<React.SetStateAction<string>>,
    institute: Institution,
    userType: "customer" | "independent" | "student" | null,
    timezone: string,
    setTimezone: React.Dispatch<React.SetStateAction<string>>,
    setShowContact: React.Dispatch<React.SetStateAction<boolean>>,
    setReason: React.Dispatch<React.SetStateAction<string>>,
    countries?: Record<string, string>,
    token: string
    setContactEmail: React.Dispatch<React.SetStateAction<string>>,
    setContactName: React.Dispatch<React.SetStateAction<string>>,
    setMessage: React.Dispatch<React.SetStateAction<string>>,
    setTitleGuid: React.Dispatch<React.SetStateAction<string>>,
}

const ProfileEdit = (props: ProfileEditProps) => {
    const [valueUpdated, setValueUpdated] = useState<boolean>(false);
    const [requiredInfoError, setRequiredInfoError] = useState<Array<string>>([]);
    const [validationError, setValidationError] = useState<Array<string>>([]);
    const [displayIndex, setDisplayIndex] = useState(0);
    const [showJWTError, setShowJWTError] = useState(false);
    const [showSaveProfileModal, setShowSaveProfileModal] = useState<boolean>(false);
    const timezoneURL = process.env.NODE_ENV === 'production' ? APPSETTINGS.production.dataTranslation : APPSETTINGS.development.dataTranslation;
    const {data: appData, setValidationFlags} = useContext(userDataCtx);
    const [appComponent, appCompNeeded] = getProfileEditDisplay(props);

    const CONFIG = require('../config.json')[process.env.NODE_ENV];

    useEffect(() => {
        props.setTitleGuid('05e278c6-6192-4636-b37e-2ea05aa46a0f');
    }, []);

    useEffect(() => {
        props.translate();
        translateTimezones();
    }, [props.lang])

    const translateTimezones = () => {
        var langLower = props.lang ? props.lang.toLowerCase() : 'en-us';
        fetch(timezoneURL + `timezones-${langLower}.json`)
            .then((res) => res.json())
            .then((data) => {
                for (var timezone of data) {
                    var timezoneClass = timezone[0].replace(/\//g, '-');
                    $(`.timezone-select-${timezoneClass}`).text(timezone[1]);
                };
            })
    }

    const cancelEdit = async () => {
        let resp = await props.getJWTDriver(false);
        if (!resp) {
            setShowJWTError(true);
        }
    }

    const instituteDisplay = () => {
        return (
            <>
                <div className="row">
                    <div className="col-sm-6">
                        <p>
                            <span className="3067d2a7-571e-4195-b6f0-a4d2215352d1">Institution Name:</span> {props.institute.name}
                        </p>
                    </div>
                    <div className="col-sm-6">
                        <p>
                            <span className="70f0e5b9-688c-438a-9c51-1d60b531aab5">Institution Address Line 1:</span> {props.institute.address1}
                        </p>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-6">
                        <p>
                            <span className="72aa28bd-f660-429f-9a86-8cd19e199adf">Institution Address Line 2:</span> {props.institute.address2}
                        </p>
                    </div>
                    <div className="col-sm-6">
                        <p>
                            <span className="a70ac1ec-051c-4e7f-a8f5-6ad76ffa5ed5">Institution City:</span> {props.institute.city}
                        </p>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-6">
                        <p>
                            <span className="f2563706-07f8-4ae3-87e6-a812cc73419e">Institution State:</span> {props.institute.state}
                        </p>
                    </div>
                    <div className="col-sm-6">
                        <p>
                            <span className="d6c2cb84-1449-4cf0-bdb9-fd267b9d3453">Institution Country:</span> {props.countries ? props.countries[props.institute.country.toLowerCase()] : props.institute.country}
                        </p>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-6">
                        <p>
                            <span className="a1bcc616-89aa-46ae-ad20-a7326112c864">Institution Zipcode:</span> {props.institute.zipCode}
                        </p>
                    </div>
                </div>
            </>)
    }

    const facilityDisplay = () => {
        return (<>
            <div className="row">
                <div className="col-sm-6">
                    <p>
                        <span className="db339b59-6aba-4a19-9042-bd60c076e2bc">Facility name:</span> {props.facilityInfo[displayIndex].facilityName}
                    </p>
                </div>
                <div className="col-sm-6">
                    <p>
                        <span className="2e59ed8e-afd6-4b93-92a5-eaf40f39a7f9">Facility address:</span> {props.facilityInfo[displayIndex].facilityAddress}
                    </p>
                </div>
            </div>
            <div className="row">
                <div className="col-sm-6">
                    <p>
                        <span className="1c4a98f9-1402-4118-beca-6a0ba6086491">Facility city:</span> {props.facilityInfo[displayIndex].facilityCity}
                    </p>
                </div>
                <div className="col-sm-6">
                    <p>
                        <span className="548a1152-3a22-4087-8d70-f90d2ba88d51">Facility state:</span> {props.facilityInfo[displayIndex].facilityState}
                    </p>
                </div>
            </div>
            <div className="row">
                <div className="col-sm-6">
                    <p>
                        <span className="3352fe31-c787-4655-a9ca-5c91d68ef94a">Facility country:</span> {props.countries ? [props.facilityInfo[displayIndex].facilityCountry.toLowerCase()] : props.facilityInfo[displayIndex].facilityCountry}
                    </p>
                </div>
                <div className="col-sm-6">
                    <p>
                        <span className="bd66450d-20b9-4d53-8316-3cdae9fc5b59">Facility Zipcode:</span> {props.facilityInfo[displayIndex].facilityZip}
                    </p>
                </div>
            </div>
        </>)
    }

    const submitEdit = async () => {
        var currentErrors: Array<string> = [];
        var validationFlag = false;
        setShowJWTError(false);

        if (!props.hideFields?.includes("firstName") && !props.optionalFields?.includes("firstName") && (!props.basicInfo.firstName || !verifyName(props.basicInfo.firstName))) {
            currentErrors = [...currentErrors, "firstName"];
            validationFlag = true;
        }
        if (!props.hideFields?.includes("lastName") && !props.optionalFields?.includes("lastName") && (!props.basicInfo.lastName || !verifyName(props.basicInfo.lastName))){
            currentErrors = [...currentErrors, "lastName"];
            validationFlag = true;
        }
        if (!props.hideFields?.includes("phoneNumber") && !props.optionalFields?.includes("phoneNumber") && (!props.basicInfo.phoneNumber || !verifyPhone(props.basicInfo.phoneNumber))) {
            currentErrors = [...currentErrors, "phoneNumber"];
            validationFlag = true;
        }
        if (!props.hideFields?.includes("mobileNumber") && props.basicInfo.mobileNumber && !verifyPhone(props.basicInfo.mobileNumber)) {
            currentErrors = [...currentErrors, "mobileNumber"];
            validationFlag = true;
        }
        if (!props.hideFields?.includes("faxNumber") && props.basicInfo.faxNumber && !verifyPhone(props.basicInfo.faxNumber)) {
            currentErrors = [...currentErrors, "faxNumber"];
            validationFlag = true;
        }
        if(appCompNeeded && !isAppDataValid(props.appId, appData, setValidationFlags)){
            validationFlag = true;
        }

        setValidationError(currentErrors);        
        
        if (
            validationFlag 
            || props.basicInfo.firstName === "" 
            || props.basicInfo.lastName === "" || ( !props.hideFields?.includes("phoneNumber") && props.basicInfo.phoneNumber === "" && !props.optionalFields?.includes("phoneNumber")) 
            || (!props.hideFields?.includes("position") && props.basicInfo.position === "" && !props.optionalFields?.includes("position"))
            || (!props.hideFields?.includes("facilityZip") && props.facZip === "" && !props.optionalFields?.includes("facilityZip"))
            || (props.bdBioSciencesUserType === "" && props.appId === CONFIG.bdBiosciences)
        ) {
            currentErrors = [];
            if (!props.hideFields?.includes("firstName") && !props.optionalFields?.includes("firstName") && props.basicInfo.firstName === '' && !currentErrors.includes("firstName")) {
                currentErrors = [...currentErrors, "firstName"];
            }
            if (!props.hideFields?.includes("lastName") && !props.optionalFields?.includes("lastName") && props.basicInfo.lastName === '' && !currentErrors.includes("lastName")) {
                currentErrors = [...currentErrors, "lastName"];
            }
            if (!props.hideFields?.includes("phoneNumber") && !props.optionalFields?.includes("phoneNumber") && props.basicInfo.phoneNumber === '' && !currentErrors.includes("phone number")) {
                currentErrors = [...currentErrors, "phoneNumber"];
            }
            if (!props.hideFields?.includes("position") && !props.optionalFields?.includes("position") && props.basicInfo.position === '' && !currentErrors.includes("position")) {
                currentErrors = [...currentErrors, "position"];
            }
            if (!props.hideFields?.includes("facilityZip") && !props.optionalFields?.includes("facilityZip") && props.facZip === '' && !currentErrors.includes("zipCode")) {
                currentErrors = [...currentErrors, "zipCode"];
            }
            if (props.bdBioSciencesUserType === "" && !currentErrors.includes("bioscienceUser") && props.appId === CONFIG.bdBiosciences) {
                currentErrors = [...currentErrors, "bioscienceUser"];
            }
            setRequiredInfoError([...currentErrors]);
            console.log("validation Error");
        } else {
            console.log("Saving the edit profile data")
            setRequiredInfoError([]);

            props.setBackgroundWIP(true);
            var body: any = {
                id: props.objectId,
                country: props.userCountry,
                email: props.email,
                firstName: props.basicInfo.firstName,
                lastName: props.basicInfo.lastName,
                phoneNumber: props.basicInfo.phoneNumber,
                defaultLanguage: props.lang,
                timezone: props.timezone,
                global: {
                    "mobileNumber": props.basicInfo.mobileNumber,
                    "faxNumber": props.basicInfo.faxNumber,
                    "position": props.basicInfo.position,
                    "accountNumber": props.basicInfo.accountNumber,
                    "zipCode": props.facZip,
                    "department": props.basicInfo.department
                }
            };

            if (props.appId === CONFIG.bdBiosciences) {
                body.applications = {
                    [props.appId!]: {
                        "BDBioSciencesUserType": props.bdBioSciencesUserType
                    }
                }
            }
            else if(doesAppRequireAdditionalClaims(props.appId)) {
               body.applications = {
                  [props.appId!]: appData
               }
            }

            try {
                const status = await API.POST(body, Endpoints.SaveUserData, props.token);
                if (!status) {
                    console.log("Save user error");
                    props.setBackgroundWIP(false);
                } else {
                    let resp = await props.getJWTDriver(false);
                    if (!resp) {
                        setShowJWTError(true);
                    }
                }
            } catch (e) {
                props.setBackgroundWIP(false);
            }
        }
    }

    const clickResetPassword = async () => {
        if (valueUpdated)
            setShowSaveProfileModal(true);
        else
            startPasswordReset();
    }

    const startPasswordReset = async () => {
        props.setBackgroundWIP(true);
        const response = await props.getJWTDriver(true);
        console.log(response);
    }

    return (
        <div>
            <div className="register-main">
                <div className="step">
                    <div className="header">
                        <div className="step-place">
                        </div>
                        <h2 className="5bbb5257-e333-4c82-b7cc-94dd9d78fc11">Update Profile</h2>
                    </div>
                </div>
                <div className="content">
                    {showJWTError ? <div className='error 99491c4e-2d49-48d9-8177-7679742e4766'>There was an issue processing your request, please try again. If the issue persists please contact support.</div> : <></>}
                    <p className="346dc068-00af-43ac-9f72-d6f252858b88">All fields marked as <span className='requiredIcon'>*</span> are required.</p>
                    <div id="basic-information">
                        <p className={requiredInfoError.length === 0 ? "hide" : "accent-1-text"}>
                            <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                            &nbsp;<span className="664eebfb-9b9d-488f-9778-2f09ffa5f99b">Please provide your</span> {requiredInfoError.map((value, index) => { return (index === 0 ? '' : ', ') + ($(`option#${value}`).length > 0 ? $(`#${value}`).text() : $(`#${value}`).attr('placeholder')); })}.
                        </p>
                        <p className={validationError.includes("firstName") && !requiredInfoError.includes("firstName") && !props.hideFields?.includes("firstName") && !props.optionalFields?.includes("firstName") ? "accent-1-text" : "hide"}>
                            <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                            &nbsp;<span className="3a0768df-1dcf-40f0-abbf-55e63e4fcb3e">Please ensure your first name contains only letters and hyphens.</span>
                        </p>
                        <p className={validationError.includes("lastName") && !requiredInfoError.includes("lastName") && !props.hideFields?.includes("lastName") && !props.optionalFields?.includes("lastName") ? "accent-1-text" : "hide"}>
                            <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                            &nbsp;<span className="6d929587-c859-4184-84c7-505ca2fa05ab">Please ensure your last name contains only letters and hyphens.</span>
                        </p>
                        <p className={validationError.includes("phoneNumber") && !requiredInfoError.includes("phoneNumber") && !props.hideFields?.includes("phoneNumber") && !props.optionalFields?.includes("phoneNumber") ? "accent-1-text" : "hide"}>
                            <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                            &nbsp;<span className="e8798137-a084-4bcd-860a-75ed727f4cc0">Please ensure your phone number is a valid phone number.</span>
                        </p>
                        <p className={validationError.includes("mobileNumber") && !props.hideFields?.includes("mobileNumber") ? "accent-1-text" : "hide"}>
                            <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                            &nbsp;<span className="270fc42e-1732-4840-94cb-5125c795472d">Please ensure your mobile number is a valid phone number.</span>
                        </p>
                        <p className={validationError.includes("faxNumber") && !props.hideFields?.includes("faxNumber") ? "accent-1-text" : "hide"}>
                            <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                            &nbsp;<span className="eab0bf1b-dfd3-4ad7-b211-04dbb6c2b3bd">Please ensure your fax number is a valid phone number.</span>
                        </p>
                        <form>
                            <div className="row">
                                <div className="col-sm-12">
                                    <span className="5877b28e-c675-4e6b-a5f5-071bec77bfbf">Email Address: </span> {props.email}
                                </div>
                            </div>
                            <div className="spacer"></div>
                            <div className="row">
                                <div className={!props.hideFields?.includes("firstName")? "col-sm-6" :"hide"}>
                                    <p>
                                        <label htmlFor='firstName' className='42074b15-913c-4b06-9035-77e444b5e9fb requiredLabel'>First Name</label>
                                        {!props.optionalFields?.includes("firstName")? <span className='requiredIcon'> *</span> : <></> }    
                                        <input
                                            type='text'
                                            placeholder="First Name"
                                            id="firstName"
                                            className={requiredInfoError.includes("firstName") || validationError.includes("firstName") ? 'error 42074b15-913c-4b06-9035-77e444b5e9fb' : '42074b15-913c-4b06-9035-77e444b5e9fb'}
                                            value={props.basicInfo.firstName}
                                            onChange={(e) => { setValueUpdated(true); props.setBasicInfo({ ...props.basicInfo, firstName: e.target.value }) }}
                                            required
                                        />
                                    </p>
                                </div>
                                <div className={!props.hideFields?.includes("lastName")? "col-sm-6" :"hide"}>
                                    <p>
                                        <label htmlFor='lastName' className='8ee1d75c-5cf4-409a-bf59-11e5d850254b requiredLabel'>Last Name</label>
                                        {!props.optionalFields?.includes("lastName")? <span className='requiredIcon'> *</span> : <></> }    
                                        <input
                                            type='text'
                                            placeholder="Last Name"
                                            id="lastName"
                                            className={requiredInfoError.includes("lastName") || validationError.includes("lastName") ? 'error 8ee1d75c-5cf4-409a-bf59-11e5d850254b' : '8ee1d75c-5cf4-409a-bf59-11e5d850254b'}
                                            value={props.basicInfo.lastName}
                                            onChange={(e) => { setValueUpdated(true); props.setBasicInfo({ ...props.basicInfo, lastName: e.target.value }) }}
                                            required
                                        />
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className={!props.hideFields?.includes("phoneNumber")? "col-sm-6" :"hide"}>
                                    <p className="tooltip">
                                        <label htmlFor='phoneNumber' className='4a3a2ad6-f75b-4e8c-9553-186b7904a26c '>Phone Number</label>
                                        {!props.optionalFields?.includes("phoneNumber")? <span className='requiredIcon'> *</span> : <></> }    
                                        <input
                                            type='text'
                                            placeholder="Phone Number"
                                            id="phoneNumber"
                                            className={requiredInfoError.includes("phoneNumber") || validationError.includes("phoneNumber") ? 'error 4a3a2ad6-f75b-4e8c-9553-186b7904a26c' : '4a3a2ad6-f75b-4e8c-9553-186b7904a26c'}
                                            value={props.basicInfo.phoneNumber}
                                            onChange={(e) => { setValueUpdated(true); props.setBasicInfo({ ...props.basicInfo, phoneNumber: e.target.value }) }}
                                            required
                                        />
                                        <span className="tooltiptext">This field does not affect your MFA phone number</span>
                                    </p>
                                </div>
                                <div className= {!props.hideFields?.includes("mobileNumber")? "col-sm-6" :"hide"}>
                                    <p>
                                        <label htmlFor='mobileNumber' className='271f4a0b-7fcb-4785-a882-16d4b45087fe'>Mobile Phone</label>
                                        <input
                                            type='text'
                                            placeholder="Mobile Phone- Optional"
                                            id='mobileNumber'
                                            className={validationError.includes("mobileNumber") ? 'error 271f4a0b-7fcb-4785-a882-16d4b45087fe' : '271f4a0b-7fcb-4785-a882-16d4b45087fe'}
                                            value={props.basicInfo.mobileNumber}
                                            onChange={(e) => { setValueUpdated(true); props.setBasicInfo({ ...props.basicInfo, mobileNumber: e.target.value }) }}
                                        />
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className={!props.hideFields?.includes("faxNumber")? "col-sm-6" :"hide"}>
                                    <p>
                                        <label htmlFor='faxNumber' className='c30e1d29-7ccf-4695-b722-f129414ebd36'>Fax Number</label>
                                        <input
                                            type='text'
                                            placeholder="Fax Number- Optional"
                                            id='faxNumber'
                                            className={validationError.includes("faxNumber") ? 'error c30e1d29-7ccf-4695-b722-f129414ebd36' : 'c30e1d29-7ccf-4695-b722-f129414ebd36'}
                                            value={props.basicInfo.faxNumber}
                                            onChange={(e) => { setValueUpdated(true); props.setBasicInfo({ ...props.basicInfo, faxNumber: e.target.value }) }}
                                        />
                                    </p>
                                </div>
                                <div className={!props.hideFields?.includes("position")? "col-sm-6" :"hide"}>
                                    <p>
                                        <label htmlFor='positionSelect' className='1d1d5243-811c-4e30-a61e-c06fe8ea8a46 requiredLabel'>Position</label>
                                        {!props.optionalFields?.includes("position")? <span className='requiredIcon'> *</span> : <></> }    
                                        <select name="positionsSelect"
                                            id='positionSelect'
                                            className={requiredInfoError.includes("position") ? 'error' : ''} value={props.basicInfo.position}
                                            onChange={(e: any) => { setValueUpdated(true); props.setBasicInfo({ ...props.basicInfo, position: e.target.value }) }}
                                        >
                                            <option value="" hidden disabled className="1d1d5243-811c-4e30-a61e-c06fe8ea8a46" id="position">Position</option>
                                            {props.positions.map(
                                                (pos: [string, string]) => (
                                                    <option key={pos[0]} value={pos[0]}> {pos[1]} </option>
                                                )
                                            )}
                                        </select>
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className={!props.hideFields?.includes("accountNumber")? "col-sm-6" :"hide"}>
                                    <p>
                                        <label htmlFor='accountNumber' className='c37ba348-ec9b-46ab-8918-81c4d7fa8c72'>Account number (if known)</label>
                                        <input
                                            type='text'
                                            placeholder="Account number (if known)- Optional"
                                            id='accountNumber'
                                            className='c37ba348-ec9b-46ab-8918-81c4d7fa8c72'
                                            value={props.basicInfo.accountNumber}
                                            onChange={(e) => { setValueUpdated(true); props.setBasicInfo({ ...props.basicInfo, accountNumber: e.target.value }) }}
                                        />
                                    </p>
                                </div>
                                <div className={!props.hideFields?.includes("department")? "col-sm-6" :"hide"}>
                                    <p>
                                        <label htmlFor='department' className='e1dcae07-36a4-4045-9469-835fb2b1a4eb'>Department</label>
                                        <input
                                            type='text'
                                            placeholder="Department- Optional"
                                            id='department'
                                            className='e1dcae07-36a4-4045-9469-835fb2b1a4eb'
                                            value={props.basicInfo.department}
                                            onChange={(e) => { setValueUpdated(true); props.setBasicInfo({ ...props.basicInfo, department: e.target.value }) }}
                                        />
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className={!props.hideFields?.includes("facilityZip")? "col-sm-6" :"hide"}>
                                    <p>
                                        <label htmlFor='zipCode' className='efc78a94-b051-4156-b47d-11b342f497db requiredLabel'>Zip Code</label>
                                        {!props.optionalFields?.includes("facilityZip")? <span className='requiredIcon'> *</span> : <></> }    
                                        <input
                                            type='text'
                                            placeholder="Zip Code"
                                            id='zipCode'
                                            className='efc78a94-b051-4156-b47d-11b342f497db'
                                            value={props.facZip}
                                            onChange={(e) => { setValueUpdated(true); props.setFacZip(e.target.value) }}
                                        />
                                    </p>
                                </div>
                                <div className="col-sm-6">
                                    <p>
                                        <label htmlFor='langSelect' className='e9e7095b-8b95-49eb-9b6a-7d9afbc1c8af'>Language</label>
                                        <select name="langSelect"
                                            id='langSelect'
                                            value={props.lang === null ? "en-US" : props.lang}
                                            onChange={(e: any) => { setValueUpdated(true); props.setLang(e.target.value) }}
                                        >
                                            {Object.keys(props.languageValues).map(
                                                (value) => (
                                                    <option value={value}> {props.languageValues[value]} </option>
                                                )
                                            )}
                                        </select>
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-6">
                                    <p>
                                        <label htmlFor='timezone' className='61ced6d9-7541-4954-84bd-41fdac5b3217'>Timezone</label>
                                        <select name="timezone"
                                            id='timezone'
                                            value={props.timezone}
                                            onChange={(e: any) => { setValueUpdated(true); props.setTimezone(e.target.value) }}
                                        >
                                            {arrayIannaTimeZones.map(
                                                (timezone) => (
                                                    <option className={`timezone-select-${timezone.replace(/\//g, '-')}`} value={timezone}> {timezone} </option>
                                                )
                                            )}
                                        </select>
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                <div className={props.appId === CONFIG.bdBiosciences ? "col-sm-6" : "hide"}>
                                    <p>
                                        <label htmlFor='bioscienceUserType' className='2ffc50a4-95ac-44be-abe7-cb75d77738b9'>BD Bioscience User Type</label>
                                        <select name="bioscienceUserType" id="bioscienceUserType" className={''} value={props.bdBioSciencesUserType !== null ? props.bdBioSciencesUserType : "Purchasing"} onChange={(e: any) => { setValueUpdated(true); props.setBdBioSciencesUserType(e.target.value) }}>
                                            <option value="" id="bioscienceUser" hidden disabled> BD Bioscience User Type </option>
                                            <option value="Purchasing" title="">Purchasing</option>
                                            <option value="Guest" title="">Guest</option>
                                            <option value="OrderInquiry" title="">Order Inquiry</option>
                                        </select>
                                    </p>
                                </div>
                            </div>
                            <div className="row">
                                {appCompNeeded && <div className="col-sm-12">
                                    {appComponent}
                                </div>}
                            </div>
                            <div className="row">
                                <div className='col-sm-12'>
                                    {props.userType !== 'student' ?
                                        <>
                                            <h4 className='c0c7f861-a3f7-4f7c-9f13-1b7fc02342d8'>Facilities:</h4>
                                            <select name="showFacility" id="showFacility" value={displayIndex} onChange={(e) => { setValueUpdated(true); setDisplayIndex(parseInt(e.target.value)) }}>
                                                {props.facilityInfo.map((value: Facility, index: number) => <option value={index}>{value.facilityName}</option>)}
                                            </select>
                                            <div className="spacer"></div>
                                            {facilityDisplay()}
                                        </> :
                                        <>
                                            <h4 className='64251233-f17a-45f5-9895-fdaf52900450'>Institution:</h4>
                                            {instituteDisplay()}
                                        </>}
                                </div>
                            </div>
                        </form>
                        <div className="spacer"></div>
                        <p className="buttons align-right">
                            <button className="button b52226fb-2521-4748-b54d-d8630cdef518" onClick={() => {
                                props.setReason('email');
                                props.setContactName(props.basicInfo.firstName + ' ' + props.basicInfo.lastName);
                                props.setContactEmail(props.email)
                                props.setMessage('')
                                props.setShowContact(true);
                            }}>Request Email Change</button>

                            <button className="button bbe9b3be-2199-4879-9bfb-aa2b2c32f6f7" onClick={() => {
                                props.setReason('mfa');
                                props.setContactName(props.basicInfo.firstName + ' ' + props.basicInfo.lastName);
                                props.setContactEmail(props.email)
                                props.setMessage('I want to change my MFA phone number')
                                props.setShowContact(true);
                            }}>Request MFA Phone Number Change</button>

                            <button className="button 886c39fd-7d6d-4ac9-8592-944866c2c2ca" onClick={clickResetPassword}>Change Password</button>

                            <button className="button a9268e4d-6667-4154-81f4-6c7804b03aa4" onClick={submitEdit}>Save</button>
                            <button className="button 4e336164-de78-4b9c-98c4-63b4efa40470" onClick={cancelEdit}>Cancel</button>
                        </p>
                    </div>
                </div>
            </div >

            {showSaveProfileModal ? <SaveProfileModal submitEdit={submitEdit} startPasswordReset={startPasswordReset} translate={props.translate} setShowSaveProfileModal={setShowSaveProfileModal} /> : <></>}
        </div >
    )
}

export default ProfileEdit
